import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, CheckCircle, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import ResendVerificationLink from "@/components/ResendVerificationLink";
import { getErrorMessage } from "@/lib/apiErrors";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", business_name: "", ref_code: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Populate referral code from URL/localStorage
  useEffect(() => {
    const stored = localStorage.getItem("ndr_ref");
    if (stored) setForm(f => ({ ...f, ref_code: stored }));
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      setSubmitted(true);
    } catch (err) {
      setError(getErrorMessage(err, "Registration failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="section-container py-16 flex items-center justify-center">
          <div className="w-full max-w-lg bg-white border border-slate-200 rounded-xl p-8 shadow-sm text-center">
            <Mail size={40} className="text-[#003366] mx-auto mb-3" />
            <h1 className="text-xl font-black text-[#003366] mb-2">Check Your Email</h1>
            <p className="text-[#64748B] text-sm mb-6">
              We sent a verification link to {form.email}. Click it to activate claiming ZIPs, posting jobs, and AI tools.
            </p>
            <ResendVerificationLink email={form.email} className="text-sm text-[#003366] font-semibold hover:underline" />
            <div className="mt-6">
              <button onClick={() => navigate("/portal")} className="btn-action !py-2 !px-4 !text-sm">
                Continue to Portal
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="section-container py-16 flex items-center justify-center">
        <div className="w-full max-w-lg">
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-black text-[#003366] mb-2">Create Your Account</h1>
              <p className="text-[#64748B] text-sm">Join NextDayRefund.com</p>
            </div>

            <div className="bg-[#2ECC71]/10 border border-[#2ECC71]/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-[#27AE60] font-semibold text-sm mb-2">
                <CheckCircle size={16} /> What you get after registering:
              </div>
              <ul className="space-y-1 text-[#334155] text-sm">
                <li>• Claim exclusive ZIP codes for $150/year</li>
                <li>• Partner portal to manage your listings</li>
                <li>• {'"Listed on NextDayRefund.com"'} badge for your website</li>
                <li>• Direct lead routing to your business</li>
              </ul>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg mb-5" data-testid="register-error">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Full Name *</label>
                  <input
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="John Smith"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Business Name</label>
                  <input
                    name="business_name"
                    type="text"
                    value={form.business_name}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Smith Tax Services"
                    data-testid="register-business-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Email Address *</label>
                <input
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="you@yourbusiness.com"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Password *</label>
                <div className="relative">
                  <input
                    name="password"
                    type={showPw ? "text" : "password"}
                    required
                    value={form.password}
                    onChange={handleChange}
                    className="input-field pr-12"
                    placeholder="Min. 8 characters"
                    data-testid="register-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#003366]" data-testid="register-toggle-password">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-action w-full !py-3 disabled:opacity-50"
                data-testid="register-submit-btn"
              >
                {loading ? "Creating Account..." : "Create Account & Get Started"}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-[#64748B]">
              Already have an account?{" "}
              <Link to="/login" className="text-[#003366] font-semibold hover:underline" data-testid="register-login-link">
                Sign In
              </Link>
            </div>
          </div>
          <div className="mt-6 text-center text-xs text-[#94A3B8] flex items-center justify-center gap-4">
            <Link to="/terms" className="hover:text-[#64748B] transition-colors" data-testid="register-terms-link">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-[#64748B] transition-colors" data-testid="register-privacy-link">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
